import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navigation from '../../Layouts/Navigation/Navigation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullhorn, faPlus, faTrash, faRotateLeft,
  faBell, faExclamationTriangle, faCircleInfo,
  faUsers, faShieldAlt, faGlobe, faCalendarDays
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../../utils/constants';

/* ── helpers ── */
const NIVEL_META = {
  urgente:      { label: 'Urgente',      color: 'bg-red-100 text-red-700 border-red-300',    icon: faExclamationTriangle, bar: 'bg-red-500' },
  comunicado:   { label: 'Comunicado',   color: 'bg-blue-100 text-blue-700 border-blue-300',  icon: faBullhorn,           bar: 'bg-blue-500' },
  notificacion: { label: 'Notificación', color: 'bg-green-100 text-green-700 border-green-300', icon: faBell,             bar: 'bg-green-500' },
};

const DEST_META = {
  todos:      { label: 'Todos',      icon: faGlobe,      color: 'bg-indigo-100 text-indigo-700' },
  inquilinos: { label: 'Inquilinos', icon: faUsers,      color: 'bg-amber-100 text-amber-700'   },
  seguridad:  { label: 'Seguridad',  icon: faShieldAlt,  color: 'bg-slate-100 text-slate-700'   },
};

const fmtDatetime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()} · ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T12:00:00'); // noon evita desfase de zona horaria
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()}`;
};

const getAdminId = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1])).id_usuario;
  } catch { return null; }
};

const Snack = ({ msg, type, onClose }) => {
  if (!msg) return null;
  const colors = { success: 'bg-green-500', error: 'bg-red-500', info: 'bg-blue-500' };
  return (
    <div className={`fixed top-5 right-5 z-50 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 ${colors[type] || colors.info}`}>
      <span>{msg}</span>
      <button onClick={onClose} className="ml-2 font-bold">✕</button>
    </div>
  );
};

const AdminComunicados = () => {
  const [comunicados, setComunicados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [snack, setSnack] = useState({ msg: '', type: 'success' });
  const [filterNivel, setFilterNivel] = useState('');
  const [filterDest, setFilterDest] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [form, setForm] = useState({
    titulo: '', contenido: '', nivel: 'comunicado', destinatario: 'todos',
    fecha_inicio: '', fecha_fin: ''
  });

  const showMsg = (msg, type = 'success') => setSnack({ msg, type });

  const fetchComunicados = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE_URL}/comunicados`);
      setComunicados(r.data);
    } catch { showMsg('Error al cargar comunicados', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchComunicados(); }, [fetchComunicados]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const id_admin = getAdminId();
    if (!id_admin) { showMsg('Sesión inválida', 'error'); return; }
    if (!form.titulo.trim() || !form.contenido.trim()) {
      showMsg('Completa título y contenido', 'error'); return;
    }
    try {
      await axios.post(`${BASE_URL}/comunicados`, { ...form, id_admin });
      showMsg('Comunicado publicado exitosamente');
      setForm({ titulo: '', contenido: '', nivel: 'comunicado', destinatario: 'todos' });
      setShowForm(false);
      fetchComunicados();
    } catch (e) {
      showMsg(e.response?.data?.error || 'Error al publicar', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este comunicado? Dejará de ser visible.')) return;
    try {
      await axios.delete(`${BASE_URL}/comunicados/${id}`);
      showMsg('Comunicado eliminado');
      fetchComunicados();
    } catch { showMsg('Error al eliminar', 'error'); }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(`${BASE_URL}/comunicados/${id}/restore`);
      showMsg('Comunicado restaurado');
      fetchComunicados();
    } catch { showMsg('Error al restaurar', 'error'); }
  };

  const filtered = comunicados.filter(c => {
    if (!showInactive && !c.activo) return false;
    if (filterNivel && c.nivel !== filterNivel) return false;
    if (filterDest && c.destinatario !== filterDest) return false;
    return true;
  });

  if (loading) return (
    <>
      <Navigation />
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-12 w-12 rounded-full border-b-2 border-indigo-600"></div>
      </div>
    </>
  );

  return (
    <>
      <Navigation />
      <Snack msg={snack.msg} type={snack.type} onClose={() => setSnack({ msg: '' })} />

      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto">

          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow">
              <FontAwesomeIcon icon={faBullhorn} className="h-8 w-8 text-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800">Comunicados</h1>
              <p className="text-sm text-gray-500 mt-1">
                {comunicados.filter(c => c.activo).length} activo(s) · {comunicados.length} total
              </p>
            </div>
            <button onClick={() => setShowForm(!showForm)}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors shadow">
              <FontAwesomeIcon icon={faPlus} />
              Nuevo comunicado
            </button>
          </div>

          {/* Formulario nuevo comunicado */}
          {showForm && (
            <div className="bg-white border border-indigo-200 rounded-2xl p-6 mb-6 shadow">
              <h3 className="text-lg font-bold text-indigo-800 mb-5 flex items-center gap-2">
                <FontAwesomeIcon icon={faPlus} />
                Nuevo comunicado
              </h3>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                  <input type="text" value={form.titulo}
                    onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))}
                    maxLength={255} required placeholder="Ej: Mantenimiento programado del ascensor"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contenido *</label>
                  <textarea value={form.contenido}
                    onChange={e => setForm(p => ({ ...p, contenido: e.target.value }))}
                    rows={4} required maxLength={2000}
                    placeholder="Escribe el mensaje del comunicado aquí..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none resize-none" />
                  <p className="text-xs text-gray-400 mt-1 text-right">{form.contenido.length}/2000</p>
                </div>
                {/* Periodo de vigencia */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Periodo de vigencia <span className="text-gray-400 font-normal">(opcional)</span></label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Fecha de inicio</label>
                      <input type="date" min={todayStr()}
                        value={form.fecha_inicio}
                        onChange={e => setForm(p => ({ ...p, fecha_inicio: e.target.value, fecha_fin: p.fecha_fin && p.fecha_fin < e.target.value ? '' : p.fecha_fin }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Fecha de fin</label>
                      <input type="date" min={form.fecha_inicio || todayStr()}
                        value={form.fecha_fin}
                        onChange={e => setForm(p => ({ ...p, fecha_fin: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Si no se especifica, el comunicado se mostrará siempre.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Nivel */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nivel de criticidad *</label>
                    <div className="flex flex-col gap-2">
                      {Object.entries(NIVEL_META).map(([key, meta]) => (
                        <label key={key} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all
                          ${form.nivel === key ? `${meta.color} border-2` : 'border-gray-200 hover:border-gray-300 bg-gray-50'}`}>
                          <input type="radio" name="nivel" value={key}
                            checked={form.nivel === key}
                            onChange={() => setForm(p => ({ ...p, nivel: key }))}
                            className="w-4 h-4 accent-indigo-600" />
                          <FontAwesomeIcon icon={meta.icon} className="h-4 w-4" />
                          <span className="text-sm font-semibold">{meta.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Destinatario */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mostrar para *</label>
                    <div className="flex flex-col gap-2">
                      {Object.entries(DEST_META).map(([key, meta]) => (
                        <label key={key} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all
                          ${form.destinatario === key ? `${meta.color} border-2 border-current` : 'border-gray-200 hover:border-gray-300 bg-gray-50'}`}>
                          <input type="radio" name="destinatario" value={key}
                            checked={form.destinatario === key}
                            onChange={() => setForm(p => ({ ...p, destinatario: key }))}
                            className="w-4 h-4 accent-indigo-600" />
                          <FontAwesomeIcon icon={meta.icon} className="h-4 w-4" />
                          <span className="text-sm font-semibold">{meta.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 justify-end pt-2 border-t border-gray-100">
                  <button type="button" onClick={() => setShowForm(false)}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-red-500 bg-gray-100 rounded-lg transition-colors">
                    Cancelar
                  </button>
                  <button type="submit"
                    className="px-6 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow">
                    Publicar comunicado
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Filtros */}
          <div className="bg-white rounded-2xl shadow p-4 mb-5 flex flex-wrap gap-3 items-center">
            <select value={filterNivel} onChange={e => setFilterNivel(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none">
              <option value="">Todos los niveles</option>
              <option value="urgente">Urgente</option>
              <option value="comunicado">Comunicado</option>
              <option value="notificacion">Notificación</option>
            </select>

            <select value={filterDest} onChange={e => setFilterDest(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none">
              <option value="">Todos los destinatarios</option>
              <option value="todos">Todos</option>
              <option value="inquilinos">Inquilinos</option>
              <option value="seguridad">Seguridad</option>
            </select>

            <label className="flex items-center gap-2 text-sm text-gray-600 ml-auto cursor-pointer">
              <input type="checkbox" checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
                className="w-4 h-4 accent-indigo-600 rounded" />
              Mostrar eliminados
            </label>
          </div>

          {/* Lista de comunicados */}
          <div className="space-y-4">
            {filtered.length === 0 ? (
              <div className="bg-white rounded-2xl shadow p-16 text-center text-gray-400">
                <FontAwesomeIcon icon={faBullhorn} className="h-12 w-12 mb-3 opacity-30" />
                <p className="text-lg font-medium">No hay comunicados{filterNivel || filterDest ? ' con esos filtros' : ''}</p>
              </div>
            ) : (
              filtered.map(c => {
                const nivel = NIVEL_META[c.nivel] || NIVEL_META.comunicado;
                const dest = DEST_META[c.destinatario] || DEST_META.todos;
                const isInactive = !c.activo;
                return (
                  <div key={c.id_comunicado}
                    className={`bg-white rounded-2xl shadow overflow-hidden transition-opacity ${isInactive ? 'opacity-50' : ''}`}>
                    {/* Barra de nivel */}
                    <div className={`h-1.5 ${nivel.bar}`}></div>
                    <div className="p-5">
                      <div className="flex flex-wrap gap-2 items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap gap-2 mb-2">
                            {/* Badge nivel */}
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${nivel.color}`}>
                              <FontAwesomeIcon icon={nivel.icon} className="h-3 w-3" />
                              {nivel.label}
                            </span>
                            {/* Badge destinatario */}
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${dest.color}`}>
                              <FontAwesomeIcon icon={dest.icon} className="h-3 w-3" />
                              {dest.label}
                            </span>
                            {isInactive && (
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                                Eliminado
                              </span>
                            )}
                          </div>
                          <h3 className="text-base font-bold text-gray-800 mb-1">{c.titulo}</h3>
                          <p className="text-sm text-gray-600 whitespace-pre-line">{c.contenido}</p>
                        </div>

                        {/* Acciones */}
                        <div className="flex items-center gap-2 ml-4 shrink-0">
                          {isInactive ? (
                            <button onClick={() => handleRestore(c.id_comunicado)}
                              className="flex items-center gap-1 text-xs text-green-600 hover:bg-green-50 px-2.5 py-1.5 rounded-lg transition-colors border border-green-200">
                              <FontAwesomeIcon icon={faRotateLeft} />
                              Restaurar
                            </button>
                          ) : (
                            <button onClick={() => handleDelete(c.id_comunicado)}
                              className="flex items-center gap-1 text-xs text-red-600 hover:bg-red-50 px-2.5 py-1.5 rounded-lg transition-colors border border-red-200">
                              <FontAwesomeIcon icon={faTrash} />
                              Eliminar
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="mt-3 pt-3 border-t border-gray-50 space-y-1.5">
                        {(c.fecha_inicio || c.fecha_fin) && (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
                              <FontAwesomeIcon icon={faCalendarDays} className="h-3 w-3" />
                              Vigente: {c.fecha_inicio ? fmtDate(c.fecha_inicio) : '∞'} – {c.fecha_fin ? fmtDate(c.fecha_fin) : '∞'}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <FontAwesomeIcon icon={faCalendarDays} className="h-3 w-3" />
                          <span>{fmtDatetime(c.fecha_creacion)}</span>
                          <span className="mx-1">·</span>
                          <span>Publicado por {c.admin_nombre} {c.admin_apellido}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default AdminComunicados;
